import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import QRCard from "../components/brand/QRCard";
import CardActions from "../components/brand/CardActions";
import IdentityPhotoCapture from "../components/brand/SelfieCapture";
import { shortDate } from "../lib/brand";
import { Receipt, Hourglass, Warning, Lock, Camera } from "@phosphor-icons/react";

const STATUS_LABELS = {
  pending_payment: { label: "Pending Payment", tone: "amber" },
  pending_verification: { label: "Pending Verification", tone: "amber" },
  active: { label: "Active", tone: "emerald" },
  rejected: { label: "Not approved", tone: "red" },
  expired: { label: "Expired", tone: "red" },
  suspended: { label: "Suspended", tone: "red" },
};

function StatusBanner({ status, rejection_reason }) {
  if (status === "active") return null;

  const map = STATUS_LABELS[status] || { label: status || "Unknown", tone: "amber" };
  const toneClass = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    red: "border-red-500/40 bg-red-500/10 text-red-200",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  }[map.tone];

  const icon = status === "rejected" || status === "expired" || status === "suspended"
    ? <Warning size={22} weight="duotone" />
    : <Hourglass size={22} weight="duotone" />;

  return (
    <div className={`border ${toneClass} p-5 rounded-md flex gap-3 items-start`}>
      {icon}
      <div className="flex-1">
        <p className="font-medium">Membership: {map.label}</p>

        {status === "pending_payment" && (
          <p className="text-sm opacity-90 mt-1">Complete your GCash payment to activate your membership.</p>
        )}

        {status === "pending_verification" && (
          <p className="text-sm opacity-90 mt-1">Your payment is under review. You'll be notified once approved.</p>
        )}

        {status === "rejected" && (
          <p className="text-sm opacity-90 mt-1">{rejection_reason || "Please contact support or submit a new payment."}</p>
        )}

        {status === "expired" && (
          <p className="text-sm opacity-90 mt-1">Your membership has expired. Please renew to access partner privileges.</p>
        )}

        {status === "suspended" && (
          <p className="text-sm opacity-90 mt-1">Your membership is currently suspended. Contact support.</p>
        )}

        {(status === "pending_payment" || status === "rejected") && (
          <Link to="/membership/payment" className="tsc-btn-gold inline-block mt-4">
            Complete Payment
          </Link>
        )}

        {status === "pending_verification" && (
          <Link to="/membership/payment" className="tsc-btn-outline inline-block mt-4">
            View payment details
          </Link>
        )}
      </div>
    </div>
  );
}

export default function MemberDashboard() {
  const [me, setMe] = useState(null);
  const [tx, setTx] = useState([]);
  const cardRef = useRef(null);

  const reloadMe = () => api.get("/members/me").then(({ data }) => setMe(data));

  useEffect(() => {
    reloadMe();
    api.get("/members/me/transactions")
      .then(({ data }) => setTx(data || []))
      .catch(() => setTx([]));
  }, []);

  if (!me) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <div className="px-6 py-16 text-white/60">Loading…</div>
      </div>
    );
  }

  const isActive = me.status === "active";
  const needsIdentityPhoto = !!me.needs_identity_photo;

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />

      {needsIdentityPhoto && (
        <IdentityPhotoCapture onCaptured={() => reloadMe()} />
      )}

      <section className="max-w-7xl mx-auto px-6 py-10">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Member Portal</p>
        <h1 className="mt-3 font-display text-4xl text-white">
          Welcome, {me.nickname || me.full_name?.split(" ")[0] || "Friend"}.
        </h1>

        <div className="mt-6">
          <StatusBanner status={me.status} rejection_reason={me.rejection_reason} />
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-1">
            {isActive && me.identity_photo_url && (
              <div className="mb-4 flex items-center gap-3 tsc-card p-3">
                <img src={me.identity_photo_url} alt="" className="h-14 w-14 rounded-md object-cover border border-[#D4AF37]/40" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Identity verified</p>
                  <p className="text-white/55 text-xs truncate">Shown to the merchant when your QR is scanned.</p>
                </div>
                <Camera size={18} className="text-white/30" />
              </div>
            )}

            {isActive ? (
              <>
                <QRCard
                  ref={cardRef}
                  svgId="tsc-member-qr-svg"
                  kind="member"
                  qrToken={me.qr_token}
                  refCode={me.qr_code?.slice(0, 8).toUpperCase()}
                  title="MEMBER PRIVILEGES"
                  subtitle={me.full_name}
                  badgeLabel={me.tier_name?.toUpperCase() || "MEMBER"}
                  perks={[
                    `Status: ${me.status}`,
                    me.expiry_date ? `Valid until: ${shortDate(me.expiry_date)}` : "Lifetime",
                    "Permanent QR · use at all partners",
                  ]}
                  footerLine="MEMBER · ACTIVE"
                  testId="member-qr-card"
                />

                <div className="mt-5 max-w-md mx-auto">
                  <CardActions
                    targetRef={cardRef}
                    svgElementId="tsc-member-qr-svg"
                    filenameBase={`social-circle-${(me.nickname || me.full_name || "member").toLowerCase().replace(/\s+/g, "-")}`}
                    shareTitle="My Social Circle Membership"
                    shareText="My members-only QR for The Social Circle by Cornerstone."
                    shareUrl={me.qr_url}
                    layout="grid"
                    testIdPrefix="member-card-actions"
                  />
                </div>
              </>
            ) : (
              <div className="tsc-card p-8 text-center">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0d0d0d] border border-[#D4AF37]/40 text-[#D4AF37]">
                  <Lock size={26} weight="duotone" />
                </div>
                <p className="font-display text-2xl text-white mt-4">Your QR is locked</p>
                <p className="text-white/60 text-sm mt-3">
                  Your branded member QR will be issued once your membership is <span className="text-[#D4AF37]">Active</span>.
                </p>
                {(me.status === "pending_payment" || me.status === "rejected") && (
                  <Link to="/membership/payment" className="tsc-btn-gold inline-block mt-6">
                    Complete Payment
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="grid sm:grid-cols-1 gap-4">
              <div className="tsc-card p-5">
                <Receipt size={22} className="text-[#D4AF37]" />
                <p className="text-[10px] tracking-luxe uppercase text-white/60 mt-3">Visits</p>
                <p className="font-display text-3xl text-white mt-1">{me.total_visits || 0}</p>
              </div>
            </div>

            <div className="tsc-card p-6">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Profile</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
                <div><span className="text-white/40">Full name</span><p className="text-white mt-0.5">{me.full_name}</p></div>
                <div><span className="text-white/40">Email</span><p className="text-white mt-0.5">{me.email}</p></div>
                <div><span className="text-white/40">Phone</span><p className="text-white mt-0.5">{me.phone}</p></div>
                <div><span className="text-white/40">City</span><p className="text-white mt-0.5">{me.city || "—"}</p></div>
                <div><span className="text-white/40">Status</span><p className="text-white mt-0.5 capitalize">{me.status?.replace(/_/g, " ")}</p></div>
                <div><span className="text-white/40">Member since</span><p className="text-white mt-0.5">{me.start_date ? shortDate(me.start_date) : "Not yet active"}</p></div>
                <div><span className="text-white/40">Expires</span><p className="text-white mt-0.5">{me.expiry_date ? shortDate(me.expiry_date) : "—"}</p></div>
              </div>
            </div>

            <div className="tsc-card p-6">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Recent visits</p>
              {tx.length === 0 ? (
                <p className="text-white/50 text-sm mt-4 italic">No visits yet.</p>
              ) : (
                <div className="mt-4 divide-y divide-white/5">
                  {tx.map((t) => (
                    <div key={t.id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{t.merchant?.business_name || "Partner venue"}</p>
                        <p className="text-white/50 text-xs">{shortDate(t.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] text-xs">Visit Recorded</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
